// F16 — About, rewritten for a pre-pilot business.
//
// What was deleted here, and why:
//
// - "More than POS - we partner with you to master your business through
//   data", "we're your partner in business success", "We succeed when you
//   succeed". Positioning, not fact. Replaced with what the software is.
// - The founding story about a specific local family business and their café.
//   It named a customer relationship the business cannot evidence and read as
//   a case study. The problem it described is real, so the problem stayed and
//   the anecdote went.
// - "We've been building in public and now we want all businesses to benefit
//   from our system" — an installed-base implication with nothing behind it.
// - The AI roadmap card ("our team will build predictive models tailored to
//   their specific business patterns", asterisked as not-in-any-package). A
//   vision statement for unbuilt work, on the page that is meant to say what
//   is true.
// - "powerful analytics", "Affordable Excellence", "Premium insights
//   shouldn't require premium prices" — the last one implies a published
//   price position. No figure is published.
// - "Existing Customer? Log In" as a CTA. There are no existing customers.
//
// Section order: hero, why it exists, what exists today, contact.
//
// There was a fifth section stating plainly that the business is pre-pilot.
// Removed on request: volunteering that on your own marketing site is a
// commercial choice, not an obligation. The obligation is not to claim the
// opposite — so the rule that still binds every page here is that no
// fabricated customer, count, logo or quote may appear anywhere in the suite.
// Saying nothing about traction is fine. Inventing traction is not.
import React from 'react';
import { Link } from 'react-router-dom';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, A, FONT, TYPE, SP, W, S } from '../../components/landing/tokens';
import ArchMark from '../../components/landing/ArchMark';

// auto-fit grids stack on their own below the min track, so the multi-column
// sections need no media query and mirror cleanly in RTL.
const grid = (min) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
    gap: SP.xl,
});

const AboutUsPage = () => {
    const { t } = useLanguage();

    const symptoms = [
        t('The till knows what sold. The rota does not.'),
        t('Stock is counted by hand, then checked against a report from somewhere else.'),
        t('Wages are worked out away from the takings that paid for them.'),
        t('Every question about the month starts with an export.'),
    ];

    const today = [
        {
            title: t('Two apps on the App Store'),
            body: t('Aqana POS runs the floor and Aqana Manager runs the back office. Both are live for iPhone and iPad.'),
        },
        {
            title: t('Android in review'),
            body: t('Both Android apps are with Google Play. The badge appears here the day the listing goes live.'),
        },
        {
            title: t('A browser portal'),
            body: t('The full back office opens in any browser at our login page. It reads the same data as the apps.'),
        },
        {
            title: t('Multiple languages'),
            body: t('Every screen is translated, and staff can switch language whenever they like. Layouts mirror properly for right-to-left scripts.'),
        },
    ];

    const MarkedItem = ({ children }) => (
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: SP.md }}>
            <span style={{ marginBlockStart: 3 }}>
                <ArchMark size={15} color={C.inkFaint} />
            </span>
            <span style={{ ...TYPE.body, color: C.inkMuted }}>{children}</span>
        </li>
    );

    return (
        <LandingLayout>
            {/* ---------- 1. Hero ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <div style={{ maxWidth: 760 }}>
                        <span style={S.label}>{t('ABOUT AQANA')}</span>
                        <h1 style={{ ...TYPE.displayXl, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('Built to stop the same number being typed twice.')}
                        </h1>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, maxWidth: W.prose, marginBlock: `${SP.lg}px 0` }}>
                            {t('Aqana is a till, a table-service app, a kitchen screen and a back office that share one database. It is built by a small independent team, founded by a data scientist.')}
                        </p>
                    </div>
                </div>
            </section>

            {/* ---------- 2. Why it exists ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={{ ...S.container, ...grid(400), gap: SP['4xl'], alignItems: 'start' }}>
                    <div>
                        <span style={S.label}>{t('WHY IT EXISTS')}</span>
                        <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('Two systems, and a person in between.')}
                        </h2>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px 0` }}>
                            {t('Most venues buy a till from one supplier and run the back office on spreadsheets. The two never speak, so somebody re-keys the numbers every week.')}
                        </p>
                    </div>

                    <div>
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md }}>
                            {symptoms.map((item) => <MarkedItem key={item}>{item}</MarkedItem>)}
                        </ul>
                        <p style={{ ...TYPE.body, color: C.ink, borderBlockStart: `1px solid ${C.lineStrong}`, marginBlockStart: SP.xl, paddingBlockStart: SP.lg }}>
                            {t('Aqana puts all of it on one record. A sale moves the stock, lands in the day and shows up in the margin.')}
                        </p>
                    </div>
                </div>
            </section>

            {/* ---------- 3. What exists today ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <span style={S.label}>{t('TODAY')}</span>
                    <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('What exists today.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px ${SP['3xl']}px` }}>
                        {t('Written plainly so you can check it. This is the whole of what you can use right now.')}
                    </p>

                    <div style={{ ...grid(240), gap: SP['2xl'] }}>
                        {today.map((item, index) => (
                            <div key={item.title} style={{ borderBlockStart: `1px solid ${C.line}`, paddingBlockStart: SP.lg }}>
                                <span
                                    style={{
                                        fontFamily: FONT.display,
                                        fontSize: '0.8125rem',
                                        fontWeight: 600,
                                        letterSpacing: '0.06em',
                                        color: C.inkFaint,
                                        display: 'block',
                                        marginBlockEnd: SP.sm,
                                    }}
                                >
                                    {`0${index + 1}`}
                                </span>
                                <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{item.title}</h3>
                                <p style={{ ...TYPE.body, color: C.inkMuted, marginBlockStart: SP.sm }}>{item.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- 4. Contact ---------- */}
            <section
                style={{
                    ...S.section(),
                    background: C.ink,
                    color: C.paper,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <ArchMark
                    size={420}
                    color={C.paper}
                    opacity={0.05}
                    style={{ position: 'absolute', insetInlineEnd: -60, insetBlockStart: -40, pointerEvents: 'none' }}
                />
                <div style={{ ...S.container, position: 'relative', textAlign: 'center' }}>
                    <h2 style={{ ...TYPE.displayL, color: C.paper, margin: 0, textWrap: 'balance' }}>
                        {t('Ask us anything about it.')}
                    </h2>
                    <p style={{ ...TYPE.bodyL, color: A.paperMuted, maxWidth: 520, marginInline: 'auto', marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                        {t('Tell us how your venue runs and we will show you Aqana set up with your own products.')}
                    </p>
                    <Link
                        to="/contact-us"
                        style={{ ...S.btnPrimary, background: C.paper, color: C.ink, border: `1px solid ${C.paper}` }}
                    >
                        {t('Contact us')}
                    </Link>
                </div>
            </section>
        </LandingLayout>
    );
};

export default AboutUsPage;
